import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { paths } from '@/constants/paths'
import { ThemeContextProvider } from '@/contexts/ThemeContext'
import { ServerExperiments } from '@/experimentation'
import { Seo } from '@/layout/Seo'
import { useUser } from '@/services/UserService'
import { ReservationObject, TheatricalReleaseObject } from '@/types/codegen-federation'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { ShowtimeDetailsPanel } from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimeDetailsPanel'
import { ShowtimesLayout } from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimesLayout'
import { TheatricalPromotionContextProvider } from '@/views/TicketCheckoutViews/ShowtimesView/components/TheatricalPromotionContext'
import { TicketsContextProvider } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsContext'
import { TicketsPromoContextProvider } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsPromo/TicketsPromoContext'
import { ShareGuildTicketsModal } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/ShareGuildTicketsModal/ShareGuildTickets'
import TicketDiscountCodesModal from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/TicketDiscountsModal'
import { useModalRefundErrorHandler } from '@/views/TicketCheckoutViews/ShowtimesView/hooks/useModalRefundErrorHandler'
import { ErrorRefundedModal } from '@/views/TicketCheckoutViews/components/ErrorRefundModal'
import { useInitReservation } from '@/views/TicketCheckoutViews/hooks/useInitReservation'
import { getCodes, getDiscountCodes } from '@/views/TicketCheckoutViews/utils'
import { useSeoData } from './useSeoData'

interface Props {
  theatricalRelease: TheatricalReleaseObject
  theatricalSlug: string
  projectGuildScore?: number
  shouldUseDataDog?: boolean
  isEditReservationFlow?: boolean
  isSceneModal?: boolean
  experiments?: ServerExperiments
}

export const ShowtimesView: FC<Props> = ({
  theatricalRelease,
  theatricalSlug,
  isEditReservationFlow = false,
  projectGuildScore,
  isSceneModal,
  experiments,
}) => {
  const [isShareGuildCodesModalOpen, setIsShareGuildCodesModalOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const calendarRef = useRef<HTMLDivElement>(null)
  const { query, asPath, isReady, push } = useSafeRouter()
  const { reservation } = useInitReservation({
    theatricalSlug,
    skip: !isReady || query?.promo === 'claim-free-ticket',
  })

  const { displayErrorRefundModal, handleCloseErrorRefundModal } = useModalRefundErrorHandler()

  const shouldEmergencyRedirectToFandango =
    theatricalRelease?.region?.ticketsEmergencyRedirectUrl && theatricalRelease?.region?.redirectTicketsPage
  const seoData = useSeoData(theatricalRelease, asPath)
  const isGuildShare = query?.flow === 'guild-share'
  const isGiftFlow = query?.flow === 'guild-gift' || query?.flow === 'gift-tickets'

  const scrollToTop = useCallback(() => {
    calendarRef?.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  const discountCodes = useMemo(() => getDiscountCodes({ query }), [query])
  const codes = useMemo(() => getCodes({ query }), [query])
  const { isLoggedIn } = useUser()

  useEffect(() => {
    if (shouldEmergencyRedirectToFandango && isReady) {
      push(theatricalRelease?.region?.ticketsEmergencyRedirectUrl as string)
    }
  }, [isReady, push, shouldEmergencyRedirectToFandango, theatricalRelease?.region?.ticketsEmergencyRedirectUrl])

  useEffect(() => {
    if (isGuildShare && discountCodes?.length > 0) {
      setIsShareGuildCodesModalOpen(true)
    }
  }, [discountCodes?.length, isGuildShare, query.flow])

  if (shouldEmergencyRedirectToFandango)
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinnerIcon size={40} />
      </div>
    )

  return (
    <>
      <Seo
        path={`${paths.tickets.index}/${theatricalSlug}`}
        canonical={seoData.url}
        description={seoData.description}
        openGraph={seoData}
        title={seoData.title}
      />
      <TicketsContextProvider
        isEditReservationFlow={isEditReservationFlow}
        theatricalRelease={theatricalRelease}
        defaultSearchDate={new Date()}
      >
        <TicketsPromoContextProvider theatricalSlug={theatricalSlug}>
          <TheatricalPromotionContextProvider theatricalSlug={theatricalSlug} experiments={experiments}>
            <ThemeContextProvider isDarkMode={false}>
              <div
                ref={containerRef}
                className={classNames(
                  !isLoggedIn
                    ? ['fixed inset-0 !overflow-x-hidden', 'top-16', 'h-[calc(100vh-64px)]']
                    : 'absolute inset-0 !overflow-x-hidden lg:fixed top-0 md:!top-16',
                )}
              >
                {!isEditReservationFlow && (
                  <ShowtimeDetailsPanel
                    containerRef={containerRef}
                    scrollToTop={scrollToTop}
                    theatricalSlug={theatricalSlug}
                    theatricalRelease={theatricalRelease}
                    isSceneModal={isSceneModal}
                    projectGuildScore={projectGuildScore}
                  />
                )}

                <div
                  className={classNames(
                    'bottom-0 flex flex-col lg:absolute lg:left-[476px] lg:right-0 lg:top-0',
                    isEditReservationFlow && '!left-0',
                  )}
                >
                  <ShowtimesLayout
                    theatricalRelease={theatricalRelease}
                    reservation={reservation as ReservationObject}
                    theatricalSlug={theatricalSlug}
                    calendarRef={calendarRef}
                    scrollToTop={scrollToTop}
                    isEditReservationFlow={isEditReservationFlow}
                  />
                </div>
                {discountCodes?.length > 0 && !isGuildShare && isGiftFlow && (
                  <TicketDiscountCodesModal discounts={discountCodes} isGiftFlow={isGiftFlow} />
                )}
                {codes?.length > 0 && <TicketDiscountCodesModal isAutoApplied={false} discounts={codes} />}
                <ShareGuildTicketsModal
                  open={isShareGuildCodesModalOpen}
                  onClose={() => {
                    setIsShareGuildCodesModalOpen(false)
                  }}
                  theatricalSlug={theatricalSlug}
                  projectName={theatricalRelease?.title as string}
                />

                <ErrorRefundedModal isOpen={displayErrorRefundModal} onClose={handleCloseErrorRefundModal} />
              </div>
            </ThemeContextProvider>
          </TheatricalPromotionContextProvider>
        </TicketsPromoContextProvider>
      </TicketsContextProvider>
    </>
  )
}
